import { Db } from 'mongodb'
import { schema, types } from 'papr'
import { VISIBILITY } from '../shared'
import { PROJECT_DELETION_TTL } from '../shared/constants'

export const SIDEBAR_ITEM_STATUS = ['PUBLISHED' as const, 'UNPUBLISHED' as const] as const

export const PAGE_FORMAT = ['MDX' as const, 'MD' as const] as const

export type PageStatus = (typeof SIDEBAR_ITEM_STATUS)[number]
export type PageFormat = (typeof PAGE_FORMAT)[number]

const collectionName = 'docspage'

export const docsPagesSchema = schema(
	{
		/**
		 * The id of the project this page belongs to.
		 */
		projectId: types.objectId({ required: true }),
		/**
		 * The slug of the guide this page belongs to.
		 */
		guideSlug: types.string({ required: true }),
		/**
		 * The slug of the version this page belongs to.
		 */
		versionSlug: types.string({ required: true }),
		/**
		 * The status of the page.
		 */
		status: types.enum(SIDEBAR_ITEM_STATUS, { required: true }),
		/**
		 * The path for this page.
		 * It does not include slugs of the guide or version.
		 *
		 * @example
		 * '/root-page/sub-page-1/sub-sub-page-1'
		 */
		path: types.string({ required: true }),
		/**
		 * The format this page uses for its `content`.
		 */
		format: types.enum(PAGE_FORMAT),
		visibility: types.enum(VISIBILITY, { required: true }),
		ogMetaData: types.object(
			{
				image: types.string(),
			},
			{ required: true },
		),
		seo: types.object(
			{
				title: types.string(),
				description: types.string(),
				shouldNotIndex: types.boolean(),
			},
			{ required: true },
		),
		published: types.object({
			content: types.string(),
			title: types.string({ required: true }),
			description: types.string(),
		}),
		draft: types.object(
			{
				content: types.string(),
				title: types.string(),
				description: types.string(),
			},
			{ required: true },
		),
		/**
		 * The id of the page that this page has been forked from.
		 */
		forkedFromPageId: types.objectId(),
		/**
		 * The time when the page was soft deleted.
		 * User can request to restore the page for a small duration. After that, the page will be permanently deleted.
		 */
		deletedAt: types.date(),
	},
	{
		timestamps: true,
	},
)

export const createDocsProjectPagesIndex = async (db: Db) => {
	await db.createIndex(
		collectionName,
		{
			deletedAt: 1,
		},
		{
			expireAfterSeconds: PROJECT_DELETION_TTL,
		},
	)
}

export type DocsPagesDocument = (typeof docsPagesSchema)[0]
export type DocsPagesSchemaOptions = (typeof docsPagesSchema)[1]
